import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AuthContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// User permissions by role
const USER_PERMISSIONS = {
  user: [
    'dashboard', 'page-equipment', 'page-events', 'page-contact',
    'reviews', 'messages', 'feedbacks'
  ],
  admin: '*' // All permissions
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const checkAuth = useCallback(async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/me`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
      } else {
        setUser(null);
      }
    } catch (err) {
      console.error('Auth check failed:', err);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async () => {
    // Check if we're on Emergent platform or standalone deployment
    const isEmergentPlatform = window.location.hostname.includes('emergentagent.com') || 
                               window.location.hostname.includes('preview.emergent');
    
    if (isEmergentPlatform) {
      // Use Emergent Auth
      const redirectUrl = window.location.origin + '/lina';
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    } else {
      // Use standalone Google OAuth
      try {
        const response = await fetch(`${BACKEND_URL}/api/auth/google/login`);
        if (response.ok) {
          const data = await response.json();
          window.location.href = data.auth_url;
        } else {
          console.error('Failed to get Google auth URL');
          setError('Authentication not configured');
        }
      } catch (err) {
        console.error('Login error:', err);
        setError('Authentication service unavailable');
      }
    }
  };

  // Local login with email and password
  const loginLocal = async (email, password) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/local/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password })
      });
      
      if (response.ok) {
        const data = await response.json();
        await checkAuth(); // Refresh user data
        return { success: true, user: data.user };
      } else {
        const errorData = await response.json();
        return { success: false, error: errorData.detail || 'Erreur de connexion' };
      }
    } catch (err) {
      console.error('Local login error:', err);
      return { success: false, error: 'Service de connexion indisponible' };
    }
  };

  const logout = async () => {
    try {
      await fetch(`${BACKEND_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      setUser(null);
      window.location.href = '/';
    }
  };

  const refreshUser = async () => {
    await checkAuth();
  };

  // Check if user has permission for a specific tab
  const hasPermission = (tabId) => {
    if (!user) return false;
    const role = user.role || (user.is_admin ? 'admin' : 'user');
    const permissions = USER_PERMISSIONS[role];
    if (permissions === '*') return true;
    return permissions.includes(tabId);
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      loading, 
      error, 
      login,
      loginLocal,
      logout, 
      refreshUser,
      setUser,
      hasPermission,
      isAuthenticated: !!user,
      isAdmin: user?.is_admin || user?.role === 'admin',
      isApproved: user?.is_approved || false,
      userRole: user?.role || (user?.is_admin ? 'admin' : 'user')
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
